import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import api from "./lib/api";
import Header from "./components/Header";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import AuthModal from "./components/AuthModal";
import MembershipModal from "./components/MembershipModal";
import Home from "./pages/Home";
import Listings from "./pages/Listings";
import PropertyDetail from "./pages/PropertyDetail";
import PostProperty from "./pages/PostProperty";
import Saved from "./pages/Saved";
import MarketWatch from "./pages/MarketWatch";
import Admin from "./pages/Admin";
import { PaymentSuccess, PaymentCancel } from "./pages/PaymentResult";
import { Toaster } from "./components/ui/sonner";
import { Loader2 } from "lucide-react";

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    api
      .post("/auth/session", { session_id: sessionId })
      .then((res) => {
        setUser(res.data.user);
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      })
      .catch(() => {
        window.history.replaceState(null, "", "/");
        navigate("/", { replace: true });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex h-screen items-center justify-center" data-testid="auth-callback-loading">
      <Loader2 className="h-10 w-10 animate-spin text-blue-600" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/properties" element={<Listings />} />
          <Route path="/property/:id" element={<PropertyDetail />} />
          <Route path="/post-property" element={<PostProperty />} />
          <Route path="/saved" element={<Saved />} />
          <Route path="/market-watch" element={<MarketWatch />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
      <AuthModal />
      <MembershipModal />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
